import React, { useState } from 'react';
import styled from 'styled-components';

import { apiClient } from '../utils/api';
import { Team, Player } from '../types';
import { 
  Card, 
  Button, 
  Input,
  colors,
  spacing,
  typography,
  shadows,
  borderRadius,
  media
} from '../styles/GlobalStyles';
import { 
  Users, 
  UserCheck
} from 'lucide-react';
import toast from 'react-hot-toast';

const AssignmentContainer = styled(Card)`
  padding: ${spacing[4]};
  margin-bottom: ${spacing[4]};
  border: 2px solid ${colors.info};
  background: ${colors.grey[900]};

  ${media.tablet} {
    padding: ${spacing[6]};
    margin-bottom: ${spacing[6]};
  }
`;

const AssignmentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[4]};
`;

const AssignmentTitle = styled.h3`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
`;

const PlayerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[4]};
  padding: ${spacing[3]};
  background: ${colors.grey[800]};
  border-radius: ${borderRadius.md};

  ${media.tablet} {
    flex-direction: row;
    align-items: center;
    gap: ${spacing[4]};
    padding: ${spacing[4]};
  }
`;

const PlayerDetails = styled.div`
  flex: 1;
`;

const PlayerName = styled.h4`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin-bottom: ${spacing[1]};
`;

const PlayerStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};

  ${media.tablet} {
    gap: ${spacing[4]};
  }
`;

const PositionBadge = styled.span<{ position: string }>`
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  background: ${props => colors.position[props.position as keyof typeof colors.position] || colors.grey[600]};
`;

const TeamsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[4]};

  ${media.tablet} {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
`;

const TeamCard = styled(Card)<{ selected?: boolean; disabled?: boolean }>`
  padding: ${spacing[4]};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  border: 2px solid ${props => {
    if (props.disabled) return colors.grey[700];
    return props.selected ? colors.info : colors.grey[800];
  }};
  background: ${props => {
    if (props.disabled) return colors.grey[800] + '50';
    return props.selected ? colors.info + '20' : colors.grey[950];
  }};
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : shadows.lg};
    border-color: ${props => props.disabled ? colors.grey[700] : colors.info};
  }
`;

const TeamHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${spacing[3]};
`;

const TeamInfo = styled.div`
  flex: 1;
`;

const TeamName = styled.h5`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin-bottom: ${spacing[1]};
`;

const TeamNumber = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
`;

const TeamStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${spacing[3]};
  padding-top: ${spacing[3]};
  border-top: 1px solid ${colors.grey[700]};
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing[1]};
`;

const StatValue = styled.span`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
`;

const StatLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.grey[400]};
`;

const AssignmentForm = styled.div`
  margin-top: ${spacing[4]};
  padding: ${spacing[4]};
  background: ${colors.grey[950]};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.grey[800]};
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[3]};

  ${media.tablet} {
    flex-direction: row;
    gap: ${spacing[4]};
    align-items: center;
  }
`;

const FormLabel = styled.label`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.white};
  min-width: auto;

  ${media.tablet} {
    min-width: 120px;
  }
`;

const CostInput = styled(Input)`
  width: 100%;
  text-align: center;

  ${media.tablet} {
    width: 120px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  margin-top: ${spacing[4]};

  @media (max-width: 767px) {
    position: sticky;
    bottom: 0;
    background: ${colors.grey[950]};
    padding-top: ${spacing[3]};
  }

  ${media.tablet} {
    flex-direction: row;
    justify-content: flex-end;
  }
`;

const CostQuickActions = styled.div`
  display: flex;
  gap: ${spacing[2]};
  flex-wrap: wrap;
  margin-top: ${spacing[2]};
`;

const CostQuickButton = styled(Button)`
  min-width: 62px;

  @media (max-width: 767px) {
    flex: 1;
    min-width: 0;
  }
`;

const NoTeamsMessage = styled.div`
  text-align: center;
  padding: ${spacing[8]};
  color: ${colors.grey[400]};
`;

interface TeamAssignmentProps {
  player: Player;
  teams: Team[];
  draftId?: string;
  onPlayerAssigned?: (assignedPlayer: Player) => void;
  onCancel?: () => void;
}

const TeamAssignment: React.FC<TeamAssignmentProps> = ({ 
  player, 
  teams, 
  draftId,
  onPlayerAssigned,
  onCancel
}) => {
  // const { user } = useAuth();
  
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [assignmentCost, setAssignmentCost] = useState<number>(1);
  const [loading, setLoading] = useState(false);

  const handleTeamSelect = (team: Team) => {
    // Check if team can accept the player
    if (team.players.length >= 20) {
      toast.error(`${team.name} roster is full (20/20 players)`);
      return;
    }
    
    if (team.budget.remaining < assignmentCost) {
      toast.error(`${team.name} doesn't have enough budget ($${team.budget.remaining} remaining)`);
      return;
    }

    setSelectedTeam(team);
  };

  const assignPlayer = async () => {
    if (!selectedTeam) return;

    try {
      setLoading(true);
      const response = await apiClient.players.assignToTeam({
        playerId: player.sportRadarId,
        teamId: selectedTeam._id,
        cost: assignmentCost,
        draftId: draftId
      });

      if (response.success) {
        toast.success(`Assigned ${player.name} to ${selectedTeam.name} for $${assignmentCost}`);
        onPlayerAssigned?.(player);
        
        // Refresh the page after a short delay to ensure the player list is updated
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to assign player:', error);
      toast.error('Failed to assign player to team');
    } finally {
      setLoading(false);
    }
  };

  const canAssign = selectedTeam && 
    selectedTeam.players.length < 20 && 
    selectedTeam.budget.remaining >= assignmentCost &&
    assignmentCost >= 1;

  const availableTeamsCount = teams.filter(team => 
    team.players.length < 20 && team.budget.remaining >= assignmentCost
  ).length;

  return (
    <AssignmentContainer>
      <AssignmentHeader>
        <Users size={20} />
        <AssignmentTitle>Assign Player to Team</AssignmentTitle>
      </AssignmentHeader>

      <PlayerInfo>
        <PlayerDetails>
          <PlayerName>{player.name}</PlayerName>
          <PlayerStats>
            <PositionBadge position={player.position}>
              {player.position}
            </PositionBadge>
            <span>{player.team}</span>
            <span>Proj: {player.projectedPoints?.toFixed(1)} pts</span>
            <span>Cost: ${player.cost}</span>
          </PlayerStats>
        </PlayerDetails>
      </PlayerInfo>

      {teams.length > 0 ? (
        <>
          <div style={{ 
            marginBottom: spacing[4],
            padding: spacing[3],
            background: colors.grey[800],
            borderRadius: borderRadius.md,
            border: `1px solid ${colors.grey[700]}`
          }}>
            <h4 style={{ 
              color: colors.white, 
              marginBottom: spacing[1],
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold
            }}>
              Available Teams: {availableTeamsCount}/{teams.length}
            </h4>
            <p style={{ 
              color: colors.grey[400], 
              fontSize: typography.fontSize.sm 
            }}>
              Teams with sufficient budget and roster space
            </p>
          </div>
          
          <TeamsGrid>
            {teams.map(team => {
              const isDisabled = team.players.length >= 20 || team.budget.remaining < assignmentCost;
              const isSelected = selectedTeam?._id === team._id;
              
              return (
                <TeamCard
                  key={team._id}
                  selected={isSelected}
                  disabled={isDisabled}
                  onClick={() => !isDisabled && handleTeamSelect(team)}
                >
                  <TeamHeader>
                    <TeamInfo>
                      <TeamName>{team.name}</TeamName>
                      <TeamNumber>Team #{team.teamNumber}</TeamNumber>
                    </TeamInfo>
                    {isSelected && (
                      <UserCheck size={20} color={colors.info} />
                    )}
                  </TeamHeader>

                  <TeamStats>
                    <StatItem>
                      <StatValue>${team.budget.remaining}</StatValue>
                      <StatLabel>Budget</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{team.players.length}/20</StatValue>
                      <StatLabel>Players</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{20 - team.players.length}</StatValue>
                      <StatLabel>Slots</StatLabel>
                    </StatItem>
                  </TeamStats>

                  {isDisabled && (
                    <div style={{ 
                      marginTop: spacing[2], 
                      fontSize: typography.fontSize.xs, 
                      color: colors.error 
                    }}>
                      {team.players.length >= 20 ? 'Roster Full' : 'Insufficient Budget'}
                    </div>
                  )}
                </TeamCard>
              );
            })}
          </TeamsGrid>

          {selectedTeam && (
            <AssignmentForm>
              <h4 style={{ color: colors.white, marginBottom: spacing[3] }}>
                Assign {player.name} to {selectedTeam.name}
              </h4>
              
              <FormRow>
                <FormLabel>Cost:</FormLabel>
                <CostInput
                  type="number"
                  value={assignmentCost}
                  onChange={(e) => setAssignmentCost(parseInt(e.target.value) || 1)}
                  min="1"
                  max={selectedTeam.budget.remaining}
                />
                <span style={{ color: colors.grey[400], fontSize: typography.fontSize.sm }}>
                  Minimum $1, Maximum ${selectedTeam.budget.remaining}
                </span>
              </FormRow>

              <CostQuickActions>
                {[1, 2, 5, 10].map((increment) => {
                  const next = assignmentCost + increment;
                  return (
                    <CostQuickButton
                      key={increment}
                      variant="secondary"
                      size="sm"
                      onClick={() => setAssignmentCost(Math.min(next, selectedTeam.budget.remaining))}
                      disabled={next > selectedTeam.budget.remaining}
                    >
                      +${increment}
                    </CostQuickButton>
                  );
                })}
                <CostQuickButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setAssignmentCost(selectedTeam.budget.remaining)}
                >
                  Max
                </CostQuickButton>
              </CostQuickActions>

              <FormRow>
                <FormLabel>Team Budget:</FormLabel>
                <span style={{ color: colors.white }}>
                  ${selectedTeam.budget.remaining} remaining
                </span>
              </FormRow>

              <FormRow>
                <FormLabel>After Assignment:</FormLabel>
                <span style={{ color: colors.white }}>
                  ${selectedTeam.budget.remaining - assignmentCost} remaining
                </span>
              </FormRow>

              <ActionButtons>
                <Button
                  variant="secondary"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  onClick={assignPlayer}
                  disabled={!canAssign || loading}
                  title={
                    !selectedTeam ? 'No team selected' :
                    selectedTeam.players.length >= 20 ? 'Team roster is full' :
                    selectedTeam.budget.remaining < assignmentCost ? 'Insufficient budget' :
                    assignmentCost < 1 ? 'Cost must be at least $1' :
                    'Assign player to team'
                  }
                >
                  {loading ? 'Assigning...' : `Assign for $${assignmentCost}`}
                </Button>
              </ActionButtons>
            </AssignmentForm>
          )}
        </>
      ) : (
        <NoTeamsMessage>
          <Users size={48} style={{ marginBottom: spacing[4], opacity: 0.5 }} />
          <p>No teams available</p>
        </NoTeamsMessage>
      )}
    </AssignmentContainer>
  );
};

export default TeamAssignment;
